// Planning calculators: retirement, net worth, budget, emergency fund, savings goal.
import { futureValue, paymentForFV, round2 } from "./finance-math";
import { GOV_BENEFITS } from "../financial-data/canada";

// Retirement projection: grow current savings + contributions to retirement,
// then estimate whether the nest egg + CPP/OAS supports the desired income.
export function retirement({
  currentAge,
  retirementAge,
  lifeExpectancy = 90,
  currentSavings,
  monthlyContribution,
  preReturn,
  postReturn,
  desiredMonthlyIncome,
  includeCPP = true,
  cppMonthly,
  includeOAS = true,
  oasMonthly,
  inflation = 2,
}) {
  const yearsToRetire = Math.max(0, (Number(retirementAge) || 0) - (Number(currentAge) || 0));
  const yearsInRetire = Math.max(1, (Number(lifeExpectancy) || 90) - (Number(retirementAge) || 0));
  const preRate = Math.max(0, Number(preReturn) || 0) / 100;
  const postRate = Math.max(0, Number(postReturn) || 0) / 100;

  const nestEgg = futureValue({
    rate: preRate,
    periods: yearsToRetire,
    pmt: (Number(monthlyContribution) || 0) * 12,
    pv: Number(currentSavings) || 0,
  });

  const cpp = includeCPP ? (cppMonthly ?? GOV_BENEFITS.cpp.avgMonthly) : 0;
  const oas = includeOAS ? (oasMonthly ?? GOV_BENEFITS.oas.maxMonthly65to74) : 0;
  const govMonthly = cpp + oas;
  const govAnnual = govMonthly * 12;

  const desiredAnnual = (Number(desiredMonthlyIncome) || 0) * 12;
  const fromSavingsNeeded = Math.max(0, desiredAnnual - govAnnual);

  // Capital required at retirement to fund the savings gap for the retirement span (present value of annuity).
  let capitalRequired;
  if (postRate === 0) {
    capitalRequired = fromSavingsNeeded * yearsInRetire;
  } else {
    capitalRequired = fromSavingsNeeded * ((1 - Math.pow(1 + postRate, -yearsInRetire)) / postRate);
  }

  const surplus = nestEgg - capitalRequired;
  const onTrack = surplus >= 0;

  // Extra monthly contribution needed to close any gap
  let extraMonthlyNeeded = 0;
  if (!onTrack && yearsToRetire > 0) {
    const extraLump = capitalRequired - nestEgg;
    const annualExtra = paymentForFV({ rate: preRate, periods: yearsToRetire, fv: extraLump, pv: 0 });
    extraMonthlyNeeded = Math.max(0, annualExtra / 12);
  }

  // Chart: projected nest egg over the accumulation years
  const series = [];
  let bal = Number(currentSavings) || 0;
  for (let y = 0; y <= yearsToRetire; y++) {
    series.push({ year: (Number(currentAge) || 0) + y, balance: round2(bal) });
    bal = bal * (1 + preRate) + (Number(monthlyContribution) || 0) * 12;
  }

  return {
    projectedNestEgg: round2(nestEgg),
    capitalRequired: round2(capitalRequired),
    surplusOrGap: round2(surplus),
    onTrack,
    govMonthly: round2(govMonthly),
    fromSavingsMonthly: round2(fromSavingsNeeded / 12),
    extraMonthlyNeeded: round2(extraMonthlyNeeded),
    yearsToRetire,
    series,
  };
}

export function netWorth({ assets = [], liabilities = [] }) {
  const totalAssets = assets.reduce((a, x) => a + (Number(x.value) || 0), 0);
  const totalLiabilities = liabilities.reduce((a, x) => a + (Number(x.value) || 0), 0);
  return {
    totalAssets: round2(totalAssets),
    totalLiabilities: round2(totalLiabilities),
    netWorth: round2(totalAssets - totalLiabilities),
    breakdown: [
      ...assets.filter((a) => Number(a.value) > 0).map((a) => ({ name: a.label, value: Number(a.value), type: "asset" })),
      ...liabilities.filter((l) => Number(l.value) > 0).map((l) => ({ name: l.label, value: Number(l.value), type: "liability" })),
    ],
  };
}

// Budget: compares income to expenses and against the 50/30/20 guideline.
export function budget({ monthlyIncome, needs = 0, wants = 0, savings = 0 }) {
  const income = Math.max(0, Number(monthlyIncome) || 0);
  const n = Math.max(0, Number(needs) || 0);
  const w = Math.max(0, Number(wants) || 0);
  const s = Math.max(0, Number(savings) || 0);
  const spent = n + w + s;
  const leftover = income - spent;
  return {
    income: round2(income),
    needs: round2(n),
    wants: round2(w),
    savings: round2(s),
    totalAllocated: round2(spent),
    leftover: round2(leftover),
    needsPct: income ? round2((n / income) * 100) : 0,
    wantsPct: income ? round2((w / income) * 100) : 0,
    savingsPct: income ? round2((s / income) * 100) : 0,
    guideline: { needs: round2(income * 0.5), wants: round2(income * 0.3), savings: round2(income * 0.2) },
  };
}

export function emergencyFund({ monthlyExpenses, monthsTarget = 3, currentSavings = 0, monthlyContribution = 0 }) {
  const exp = Math.max(0, Number(monthlyExpenses) || 0);
  const months = Math.max(0, Number(monthsTarget) || 0);
  const target = exp * months;
  const current = Math.max(0, Number(currentSavings) || 0);
  const gap = Math.max(0, target - current);
  const contrib = Math.max(0, Number(monthlyContribution) || 0);
  const monthsToGoal = contrib > 0 ? Math.ceil(gap / contrib) : null;
  return {
    targetAmount: round2(target),
    currentSavings: round2(current),
    gap: round2(gap),
    percentFunded: target ? round2(Math.min(100, (current / target) * 100)) : 0,
    monthsToGoal,
    monthsCovered: exp ? round2(current / exp) : 0,
  };
}

export function savingsGoal({ goalAmount, currentSavings = 0, years, annualReturn = 0 }) {
  const goal = Math.max(0, Number(goalAmount) || 0);
  const current = Math.max(0, Number(currentSavings) || 0);
  const yrs = Math.max(0, Number(years) || 0);
  const monthlyRate = Math.max(0, Number(annualReturn) || 0) / 100 / 12;
  const periods = yrs * 12;
  const required = periods > 0 ? paymentForFV({ rate: monthlyRate, periods, fv: goal, pv: current }) : 0;
  const totalContrib = Math.max(0, required) * periods;
  const growth = goal - current - totalContrib;

  const series = [];
  let bal = current;
  for (let y = 0; y <= yrs; y++) {
    series.push({ year: y, balance: round2(bal) });
    for (let m = 0; m < 12; m++) bal = bal * (1 + monthlyRate) + Math.max(0, required);
  }
  return {
    monthlyRequired: round2(Math.max(0, required)),
    totalContributions: round2(Math.max(0, totalContrib)),
    growthEarned: round2(Math.max(0, growth)),
    goalAmount: round2(goal),
    series,
  };
}
